/**
 * Recommendation Engine
 *
 * Generates personalized quiz recommendations based on:
 * 1. Due for review (spaced repetition)
 * 2. Needs work (<80% completion)
 * 3. Almost there (80-95% completion)
 * 4. New to you (never attempted)
 * 5. Random discovery
 *
 * Philosophy:
 * - NON-DETERMINISTIC: Recommendations change on each visit (adds randomization)
 * - NO SHAME: Frame everything positively ("Ready for a refresh?" not "You're behind")
 * - GENTLE: Make reviews inviting, not mandatory
 * - ENCOURAGING: Celebrate progress ("Almost there!")
 */

import type { KnowledgeState, QuizConfig } from "@/db/types";
import { getDaysSinceReview } from "./spacedRepetition";

export type RecommendationReason =
  | "due" // Due for review (SRS)
  | "work" // Needs practice (<80%)
  | "almost" // Almost there (80-95%)
  | "new" // Unexplored dataset
  | "shuffle"; // Mix it up (random)

export interface Recommendation {
  // Quiz info
  quiz: QuizConfig;

  // Knowledge state (if exists)
  knowledge?: KnowledgeState;

  // Recommendation metadata
  reason: RecommendationReason;
  priority: number; // Higher = more important

  // Contextual info
  completionRate?: number; // 0-1
  daysSinceLastPractice?: number;
  daysUntilDue?: number;

  // Display hints
  reasonLabel: string;
  reasonDescription: string;
}

export interface RecommendationStats {
  dueForReview: number;
  needsWork: number;
  almostThere: number;
  unexplored: number;
}

/**
 * Get recommendation statistics (for quick action cards)
 */
export function getRecommendationStats(
  knowledgeStates: KnowledgeState[],
  allQuizzes: QuizConfig[]
): RecommendationStats {
  const now = new Date();
  const twoWeeksAgo = now.getTime() - 14 * 24 * 60 * 60 * 1000;

  // Due for review: Not practiced in 2+ weeks
  const dueForReview = knowledgeStates.filter((k) => {
    const lastUpdated = new Date(k.lastUpdated).getTime();
    return lastUpdated < twoWeeksAgo;
  }).length;

  // Needs work: <80% completion
  const needsWork = knowledgeStates.filter(
    (k) => k.completionRate < 80
  ).length;

  // Almost there: 80-95% completion
  const almostThere = knowledgeStates.filter(
    (k) => k.completionRate >= 80 && k.completionRate < 95
  ).length;

  // Unexplored: Datasets never attempted
  const attemptedDatasets = new Set(
    knowledgeStates.map((k) => k.datasetSignature)
  );
  const unexplored = allQuizzes.filter(
    (q) => !attemptedDatasets.has(q.datasetSignature)
  ).length;

  return {
    dueForReview,
    needsWork,
    almostThere,
    unexplored,
  };
}

/**
 * Generate personalized recommendations
 *
 * Returns up to 8 recommendations with a mix of reasons
 */
export function getRecommendations(
  knowledgeStates: KnowledgeState[],
  allQuizzes: QuizConfig[],
  maxRecommendations: number = 8
): Recommendation[] {
  const now = new Date();
  const recommendations: Recommendation[] = [];

  // Create a map of datasetSignature -> quiz for quick lookup
  const quizMap = new Map<string, QuizConfig>();
  allQuizzes.forEach((q) => {
    quizMap.set(q.datasetSignature, q);
  });

  // ========================================================================
  // 1. DUE FOR REVIEW (30% weight - with randomization)
  // ========================================================================
  const dueForReview = knowledgeStates
    .filter((k) => {
      const daysSince = getDaysSinceReview(new Date(k.lastUpdated), now);
      // Add fuzziness: ±20% to due date (makes it feel less robotic)
      const fuzzedDueDate = 14 * (0.8 + Math.random() * 0.4);
      return daysSince >= fuzzedDueDate;
    })
    .map((k): Recommendation | null => {
      const quiz = quizMap.get(k.datasetSignature);
      if (!quiz) return null;

      const daysSince = getDaysSinceReview(new Date(k.lastUpdated), now);

      return {
        quiz,
        knowledge: k,
        reason: "due",
        priority: 3 + Math.random(), // 3.0-4.0 (high priority + random boost)
        completionRate: k.completionRate / 100,
        daysSinceLastPractice: daysSince,
        reasonLabel: "Due for Review",
        reasonDescription: `Last practiced ${daysSince} days ago. Keep it fresh!`,
      };
    })
    .filter((r): r is Recommendation => r !== null);

  recommendations.push(...dueForReview);

  // ========================================================================
  // 2. NEEDS WORK (25% weight)
  // ========================================================================
  const needsWork = knowledgeStates
    .filter((k) => k.completionRate < 80)
    .map((k): Recommendation | null => {
      const quiz = quizMap.get(k.datasetSignature);
      if (!quiz) return null;

      const gap = 80 - k.completionRate;

      return {
        quiz,
        knowledge: k,
        reason: "work",
        priority: 2 + gap / 100 + Math.random() * 0.5, // 2.0-3.3
        completionRate: k.completionRate / 100,
        reasonLabel: "Needs Practice",
        reasonDescription: `${Math.round(k.completionRate)}% mastered. Let's improve together!`,
      };
    })
    .filter((r): r is Recommendation => r !== null);

  recommendations.push(...needsWork);

  // ========================================================================
  // 3. ALMOST THERE (20% weight)
  // ========================================================================
  const almostThere = knowledgeStates
    .filter((k) => k.completionRate >= 80 && k.completionRate < 95)
    .map((k): Recommendation | null => {
      const quiz = quizMap.get(k.datasetSignature);
      if (!quiz) return null;

      const closeness = (k.completionRate - 80) / 15; // 0-1

      return {
        quiz,
        knowledge: k,
        reason: "almost",
        priority: 2 + closeness * 1.5 + Math.random() * 0.3, // 2.0-3.8
        completionRate: k.completionRate / 100,
        reasonLabel: "Almost There!",
        reasonDescription: `${Math.round(k.completionRate)}% mastered. You're so close!`,
      };
    })
    .filter((r): r is Recommendation => r !== null);

  recommendations.push(...almostThere);

  // ========================================================================
  // 4. UNEXPLORED (15% weight)
  // ========================================================================
  const attemptedDatasets = new Set(
    knowledgeStates.map((k) => k.datasetSignature)
  );

  const unexplored = allQuizzes
    .filter((q) => !attemptedDatasets.has(q.datasetSignature))
    .map((quiz): Recommendation => {
      return {
        quiz,
        reason: "new",
        priority: 1 + Math.random() * 2, // 1.0-3.0 (high randomness for discovery)
        reasonLabel: "Unexplored",
        reasonDescription: "Brand new dataset to discover!",
      };
    });

  recommendations.push(...unexplored);

  // ========================================================================
  // Sort by priority and take top N-1 (save spot for random)
  // ========================================================================
  const sorted = recommendations
    .sort((a, b) => b.priority - a.priority)
    .slice(0, maxRecommendations - 1);

  // ========================================================================
  // 5. SHUFFLE (10% weight - mix it up, always last)
  // ========================================================================
  if (allQuizzes.length > 0) {
    const randomQuiz = allQuizzes[Math.floor(Math.random() * allQuizzes.length)];
    if (randomQuiz) {
      const knowledge = knowledgeStates.find(
        (k) => k.datasetSignature === randomQuiz.datasetSignature
      );

      sorted.push({
        quiz: randomQuiz,
        knowledge,
        reason: "shuffle",
        priority: 1,
        completionRate: knowledge ? knowledge.completionRate / 100 : undefined,
        reasonLabel: "Mix It Up",
        reasonDescription: "Sometimes random is the best choice!",
      });
    }
  }

  // ========================================================================
  // Final shuffle (keeps it feeling alive)
  // ========================================================================
  return sorted.sort(() => Math.random() - 0.5);
}

/**
 * Get a single random quiz (for "Surprise Me!" button)
 */
export function getRandomQuiz(
  allQuizzes: QuizConfig[],
  knowledgeStates?: KnowledgeState[]
): Recommendation | null {
  if (allQuizzes.length === 0) return null;

  const randomQuiz = allQuizzes[Math.floor(Math.random() * allQuizzes.length)];
  if (!randomQuiz) return null;

  const knowledge = knowledgeStates?.find(
    (k) => k.datasetSignature === randomQuiz.datasetSignature
  );

  return {
    quiz: randomQuiz,
    knowledge,
    reason: "shuffle",
    priority: 1,
    completionRate: knowledge ? knowledge.completionRate / 100 : undefined,
    reasonLabel: "Surprise Quiz",
    reasonDescription: "Let's see what fate chose for you!",
  };
}

/**
 * Get reason badge metadata for display
 */
export function getReasonBadge(reason: RecommendationReason): {
  label: string;
  color: string;
  emoji: string;
} {
  switch (reason) {
    case "due":
      return {
        label: "Due for Review",
        color: "orange",
        emoji: "🔄",
      };
    case "work":
      return {
        label: "Needs Practice",
        color: "red",
        emoji: "💪",
      };
    case "almost":
      return {
        label: "Almost There!",
        color: "yellow",
        emoji: "🎯",
      };
    case "new":
      return {
        label: "Unexplored",
        color: "blue",
        emoji: "✨",
      };
    case "shuffle":
      return {
        label: "Mix It Up",
        color: "purple",
        emoji: "🎲",
      };
  }
}
